import React from "react";
import { Collapse } from "@/components/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faList,
  faPlay,
  faFolder,
  faFolderOpen,
  faChevronRight,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { getSourceFromPath } from "@/utils/tracker-helpers";
import { SourceIcon } from "@/components/resource-card";
import { TrackerViewProps } from "../types";
import { useTrackerActions } from "../hooks/useTrackerActions";
import TrackerTagManagerInline from "../components/tracker-tag-manager-inline";
import GroupHeader from "@/components/group-header";
import { Tooltip, IconButton, Chip, Switch } from "@/components/ui";
import { Tracker } from "@/models/tracker";
import ResourceGroupCard from "@/components/resource-group-card";
import { ResourceGroup, getGroupRefId } from "@/models/resource-group";
import { trackerGalleryConfig } from "@/config/resource-gallery-configs";
import type { ViewSize } from "@/components/layout/gallery-layout/types";

/**
 * Arc-style Tracker Table View
 *
 * Displays trackers in a table format with selection, sorting, and grouping.
 */

const CELL_PADDING: Record<ViewSize, string> = {
  small: "px-2 py-1",
  medium: "px-3 py-2.5",
  large: "px-4 py-4",
};

const TEXT_SIZE: Record<ViewSize, string> = {
  small: "text-xs",
  medium: "text-sm",
  large: "text-base",
};

const ROW_HEIGHT: Record<ViewSize, string> = {
  small: "",
  medium: "",
  large: "h-16",
};

const TrackerTableView: React.FC<TrackerViewProps> = ({
  trackers,
  onToggleActive,
  onOpenExecutions,
  getTrackerValue,
  trackersTagsMap,
  onTagsUpdate,
  groupBy,
  groupedTrackers,
  collapsedGroups = new Set(),
  onToggleGroup,
  resourceGroups,
  expandedFolders,
  onToggleFolder,
  viewSize = "medium",
}) => {
  const { runTracker, isRunning } = useTrackerActions();
  const cellPad = CELL_PADDING[viewSize];
  const textSize = TEXT_SIZE[viewSize];

  const handleRunTracker = async (trackerId: string) => {
    await runTracker(trackerId);
  };

  const isSmall = viewSize === "small";
  const isLarge = viewSize === "large";

  const renderTable = (trackersToRender: Tracker[]) => (
    <div className="bg-surface-elevated rounded-arc-lg border border-arc-border overflow-hidden shadow-arc-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-surface-secondary border-b border-arc-border">
              <th className={`${cellPad} text-left ${textSize} font-semibold text-content-primary`}>
                Nom
              </th>
              <th className={`${cellPad} text-left ${textSize} font-semibold text-content-primary`}>
                Source
              </th>
              {!isSmall && (
                <th className={`${cellPad} text-left ${textSize} font-semibold text-content-primary`}>
                  Tags
                </th>
              )}
              {!isSmall && (
                <th className={`${cellPad} text-left ${textSize} font-semibold text-content-primary`}>
                  Type
                </th>
              )}
              <th className={`${cellPad} text-left ${textSize} font-semibold text-content-primary`}>
                Statut
              </th>
              <th className={`${cellPad} text-left ${textSize} font-semibold text-content-primary`}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {trackersToRender.map((tracker) => {
              const trackerValue = getTrackerValue(tracker);
              const source = getSourceFromPath(tracker);

              return (
                <tr
                  key={tracker.id}
                  className={`border-b border-arc-border last:border-b-0 transition-colors duration-arc-fast hover:bg-surface-secondary ${ROW_HEIGHT[viewSize]}`}
                >
                  <td className={cellPad}>
                    <span className={`${textSize} font-medium text-content-primary`}>
                      {trackerValue?.value || tracker.id}
                    </span>
                  </td>
                  <td className={cellPad}>
                    <div className="flex items-center gap-2">
                      <SourceIcon source={source} size="sm" />
                      <span className={`${textSize} text-content-secondary`}>
                        {source || "N/A"}
                      </span>
                    </div>
                  </td>
                  {!isSmall && (
                    <td className={cellPad}>
                      <TrackerTagManagerInline
                        tracker={tracker}
                        trackersTagsMap={trackersTagsMap}
                        onTagsUpdate={onTagsUpdate}
                      />
                    </td>
                  )}
                  {!isSmall && (
                    <td className={cellPad}>
                      <Chip
                        variant="soft"
                        color={tracker.is_import ? "info" : "success"}
                        size="sm"
                      >
                        {tracker.is_import ? "Import" : "Monitoring"}
                      </Chip>
                    </td>
                  )}
                  <td className={cellPad}>
                    {!tracker.is_import && (
                      <Switch
                        checked={tracker.status === "active"}
                        onChange={(checked) =>
                          onToggleActive(tracker.id, checked)
                        }
                        size="sm"
                      />
                    )}
                  </td>
                  <td className={cellPad}>
                    <div className="flex items-center gap-1">
                      {!tracker.is_import && (
                        <Tooltip content="Exécuter le tracker">
                          <IconButton
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRunTracker(tracker.id)}
                            disabled={isRunning}
                            className="text-success hover:bg-success-light/20"
                          >
                            <FontAwesomeIcon icon={faPlay} size="sm" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip content="Voir les exécutions">
                        <IconButton
                          variant="ghost"
                          size="sm"
                          onClick={() => onOpenExecutions(tracker)}
                        >
                          <FontAwesomeIcon icon={faList} size="sm" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const colCount = isSmall ? 4 : 6;

  const renderTableWithFolders = (
    trackersToRender: Tracker[],
    foldersToRender?: ResourceGroup[],
    filterByGroup?: (tracker: Tracker) => boolean,
  ) => {
    const combinedRows: React.ReactNode[] = [];

    // Helper to add folders recursively
    const addFoldersRecursively = (
      parentId: string | null,
      level: number = 0,
      folders: ResourceGroup[] = foldersToRender || [],
    ) => {
      const foldersAtLevel = folders.filter((folder) => {
        const folderParentId = folder.parent?.id || null;
        return folderParentId === parentId;
      });

      foldersAtLevel.forEach((folder) => {
        let trackersInFolder = trackers.filter((t) => {
          const groupId = getGroupRefId(t.group);
          return groupId === folder.id;
        });

        // Apply group filter if provided
        if (filterByGroup) {
          trackersInFolder = trackersInFolder.filter(filterByGroup);
        }

        const isExpanded = expandedFolders?.has(folder.id) ?? false;

        // Add folder row with indentation based on level
        combinedRows.push(
          <tr
            key={`folder-${folder.id}`}
            className={`group cursor-pointer transition-all ${
              isExpanded ? "" : "hover:bg-surface-secondary"
            }`}
            style={isExpanded ? { backgroundColor: `${folder.color || "#6366F1"}1a` } : undefined}
            onClick={() => onToggleFolder?.(folder.id)}
          >
            <td colSpan={colCount} className="px-0 py-0">
              <div
                className={`${
                  isExpanded
                    ? "border-2 rounded-md overflow-hidden m-2"
                    : ""
                }`}
                style={isExpanded ? {
                  borderColor: `${folder.color || "#6366F1"}4d`,
                  backgroundColor: `${folder.color || "#6366F1"}0d`,
                } : undefined}
              >
                <div className="flex items-center px-4 py-2">
                  <div
                    className="flex items-center gap-2 flex-1"
                    style={{ paddingLeft: `${level * 20}px` }}
                  >
                    <FontAwesomeIcon
                      icon={isExpanded ? faChevronDown : faChevronRight}
                      className="text-content-secondary text-xs transition-transform duration-200"
                    />
                    <FontAwesomeIcon
                      icon={isExpanded ? faFolderOpen : faFolder}
                      style={{ color: folder.color || "#6366F1" }}
                    />
                    <span className="font-medium text-content-primary">
                      {folder.name || "Sans nom"}
                    </span>
                    <span className="text-content-secondary text-sm ml-2">
                      ({trackersInFolder.length}{" "}
                      {trackersInFolder.length === 1 ? "élément" : "éléments"})
                    </span>
                  </div>
                  <span className="text-content-secondary text-sm px-4">
                    Dossier
                  </span>
                </div>
              </div>
            </td>
          </tr>,
        );

        // If expanded, add child folders and trackers as a nested table
        if (isExpanded) {
          const nestedRows: React.ReactNode[] = [];

          // Helper to add nested folder rows
          const addNestedFolders = (
            parentId: string | null,
            currentLevel: number,
          ) => {
            const childFolders = folders.filter((f) => f.parent === parentId);

            childFolders.forEach((childFolder) => {
              const childTrackersInFolder = trackersToRender.filter(
                (t) => t.group === childFolder.id,
              );

              // Skip this folder if:
              // - No trackers match the group filter (when grouping is active)
              // - AND no child folders have matching trackers
              if (filterByGroup) {
                const hasMatchingTrackers =
                  childTrackersInFolder.some(filterByGroup);
                const hasChildFoldersWithMatches = folders.some((f) => {
                  if (f.parent === childFolder.id) {
                    return trackersToRender
                      .filter((t) => t.group === f.id)
                      .some(filterByGroup);
                  }
                  return false;
                });

                if (!hasMatchingTrackers && !hasChildFoldersWithMatches) {
                  return;
                }
              }

              nestedRows.push(
                <tr
                  key={`nested-folder-${childFolder.id}`}
                  className="hover:bg-surface-secondary cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFolder?.(childFolder.id);
                  }}
                >
                  <td colSpan={colCount} className="p-2">
                    <div
                      className="flex items-center gap-2"
                      style={{ paddingLeft: `${currentLevel * 20}px` }}
                    >
                      <FontAwesomeIcon
                        icon={
                          expandedFolders?.has(childFolder.id)
                            ? faChevronDown
                            : faChevronRight
                        }
                        className="text-content-secondary text-xs"
                      />
                      <FontAwesomeIcon
                        icon={
                          expandedFolders?.has(childFolder.id)
                            ? faFolderOpen
                            : faFolder
                        }
                        className="text-sm"
                        style={{ color: childFolder.color || "#f59e0b" }}
                      />
                      <span className="font-medium text-sm">
                        {childFolder.name || "Sans nom"}
                      </span>
                      <span className="text-content-secondary text-xs">
                        ({childTrackersInFolder.length})
                      </span>
                    </div>
                  </td>
                </tr>,
              );

              if (expandedFolders?.has(childFolder.id)) {
                addNestedFolders(childFolder.id, currentLevel + 1);

                // Add trackers in child folder
                childTrackersInFolder.forEach((tracker) => {
                  const trackerValue = getTrackerValue(tracker);
                  const source = getSourceFromPath(tracker);

                  nestedRows.push(
                    <tr
                      key={`nested-tracker-${tracker.id}`}
                      className="hover:bg-surface-secondary"
                    >
                      <td className={cellPad}>
                        <div style={{ paddingLeft: `${(currentLevel + 1) * 20}px` }}>
                          <span className={`${textSize} font-medium`}>{trackerValue?.value || tracker.id}</span>
                        </div>
                      </td>
                      <td className={cellPad}>
                        <div className="flex items-center gap-2">
                          <SourceIcon source={source} size="sm" />
                          <span className={`${textSize} text-content-secondary`}>{source || "N/A"}</span>
                        </div>
                      </td>
                      {!isSmall && (
                        <td className={cellPad}>
                          <TrackerTagManagerInline tracker={tracker} trackersTagsMap={trackersTagsMap} onTagsUpdate={onTagsUpdate} />
                        </td>
                      )}
                      {!isSmall && (
                        <td className={cellPad}>
                          <Chip variant="soft" color={tracker.is_import ? "info" : "success"} size="sm">
                            {tracker.is_import ? "Import" : "Monitoring"}
                          </Chip>
                        </td>
                      )}
                      <td className={cellPad}>
                        {!tracker.is_import && (
                          <Switch checked={tracker.status === "active"} onChange={(checked) => onToggleActive(tracker.id, checked)} size="sm" />
                        )}
                      </td>
                      <td className={cellPad}>
                        <div className="flex items-center gap-1">
                          {!tracker.is_import && (
                            <Tooltip content="Exécuter le tracker">
                              <IconButton size="sm" variant="ghost" onClick={() => handleRunTracker(tracker.id)} disabled={isRunning} className="hover:text-success hover:bg-success-light/20">
                                <FontAwesomeIcon icon={faPlay} size="xs" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip content="Voir les exécutions">
                            <IconButton size="sm" variant="ghost" onClick={() => onOpenExecutions(tracker)} className="hover:text-primary hover:bg-primary-light/20">
                              <FontAwesomeIcon icon={faList} size="xs" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>,
                  );
                });
              }
            });
          };

          // Add child folders first
          addNestedFolders(folder.id, level + 1);

          // Add direct trackers in this folder
          trackersInFolder.forEach((tracker) => {
            const trackerValue = getTrackerValue(tracker);
            const source = getSourceFromPath(tracker);

            nestedRows.push(
              <tr key={`tracker-${tracker.id}`} className="hover:bg-surface-secondary">
                <td className={cellPad}>
                  <div style={{ paddingLeft: `${(level + 1) * 20}px` }}>
                    <span className={`${textSize} font-medium`}>{trackerValue?.value || tracker.id}</span>
                  </div>
                </td>
                <td className={cellPad}>
                  <div className="flex items-center gap-2">
                    <SourceIcon source={source} size="sm" />
                    <span className={`${textSize} text-content-secondary`}>{source || "N/A"}</span>
                  </div>
                </td>
                {!isSmall && (
                  <td className={cellPad}>
                    <TrackerTagManagerInline tracker={tracker} trackersTagsMap={trackersTagsMap} onTagsUpdate={onTagsUpdate} />
                  </td>
                )}
                {!isSmall && (
                  <td className={cellPad}>
                    <Chip variant="soft" color={tracker.is_import ? "info" : "success"} size="sm">
                      {tracker.is_import ? "Import" : "Monitoring"}
                    </Chip>
                  </td>
                )}
                <td className={cellPad}>
                  {!tracker.is_import && (
                    <Switch checked={tracker.status === "active"} onChange={(checked) => onToggleActive(tracker.id, checked)} size="sm" />
                  )}
                </td>
                <td className={cellPad}>
                  <div className="flex items-center gap-1">
                    {!tracker.is_import && (
                      <Tooltip content="Exécuter le tracker">
                        <IconButton size="sm" variant="ghost" onClick={() => handleRunTracker(tracker.id)} disabled={isRunning} className="hover:text-success hover:bg-success-light/20">
                          <FontAwesomeIcon icon={faPlay} size="xs" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip content="Voir les exécutions">
                      <IconButton size="sm" variant="ghost" onClick={() => onOpenExecutions(tracker)} className="hover:text-primary hover:bg-primary-light/20">
                        <FontAwesomeIcon icon={faList} size="xs" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </td>
              </tr>,
            );
          });

          // Add all nested content in a single row with nested table
          if (nestedRows.length > 0) {
            combinedRows.push(
              <tr key={`folder-${folder.id}-contents`}>
                <td colSpan={colCount} className="p-0">
                  <div
                    className="border-l-2 ml-2 bg-surface-secondary/20"
                    style={{ borderLeftColor: `${folder.color || "#6366F1"}33` }}
                  >
                    <table className="w-full">
                      <tbody>{nestedRows}</tbody>
                    </table>
                  </div>
                </td>
              </tr>,
            );
          }
        }
      });
    };

    // Start with root folders
    if (foldersToRender && onToggleFolder && expandedFolders) {
      addFoldersRecursively(null, 0);
    }

    // Add trackers that are not in folders
    trackersToRender.forEach((tracker) => {
      // Skip if tracker is in a folder
      if (tracker.group) return;

      // Skip if doesn't match filter
      if (filterByGroup && !filterByGroup(tracker)) return;

      const trackerValue = getTrackerValue(tracker);
      const source = getSourceFromPath(tracker);

      combinedRows.push(
        <tr
          key={tracker.id}
          className={`border-b border-arc-border last:border-b-0 transition-colors duration-arc-fast hover:bg-surface-secondary ${ROW_HEIGHT[viewSize]}`}
        >
          <td className={cellPad}>
            <span className={`${textSize} font-medium text-content-primary`}>{trackerValue?.value || tracker.id}</span>
          </td>
          <td className={cellPad}>
            <div className="flex items-center gap-2">
              <SourceIcon source={source} size="sm" />
              <span className={`${textSize} text-content-secondary`}>{source || "N/A"}</span>
            </div>
          </td>
          {!isSmall && (
            <td className={cellPad}>
              <TrackerTagManagerInline tracker={tracker} trackersTagsMap={trackersTagsMap} onTagsUpdate={onTagsUpdate} />
            </td>
          )}
          {!isSmall && (
            <td className={cellPad}>
              <Chip variant="soft" color={tracker.is_import ? "info" : "success"} size="sm">
                {tracker.is_import ? "Import" : "Monitoring"}
              </Chip>
            </td>
          )}
          <td className={cellPad}>
            {!tracker.is_import && (
              <Switch checked={tracker.status === "active"} onChange={(checked) => onToggleActive(tracker.id, checked)} size="sm" />
            )}
          </td>
          <td className={cellPad}>
            <div className="flex items-center gap-1">
              {!tracker.is_import && (
                <Tooltip content="Exécuter le tracker">
                  <IconButton size="sm" variant="ghost" onClick={() => handleRunTracker(tracker.id)} disabled={isRunning} className="hover:text-success hover:bg-success-light/20">
                    <FontAwesomeIcon icon={faPlay} size="sm" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip content="Voir les exécutions">
                <IconButton size="sm" variant="ghost" onClick={() => onOpenExecutions(tracker)} className="hover:text-primary hover:bg-primary-light/20">
                  <FontAwesomeIcon icon={faList} size="sm" />
                </IconButton>
              </Tooltip>
            </div>
          </td>
        </tr>,
      );
    });

    return (
      <div className="bg-surface-elevated rounded-arc-lg border border-arc-border overflow-hidden shadow-arc-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-surface-secondary border-b border-arc-border">
                <th className={`${cellPad} text-left ${textSize} font-semibold text-content-primary`}>Nom</th>
                <th className={`${cellPad} text-left ${textSize} font-semibold text-content-primary`}>Source</th>
                {!isSmall && <th className={`${cellPad} text-left ${textSize} font-semibold text-content-primary`}>Tags</th>}
                {!isSmall && <th className={`${cellPad} text-left ${textSize} font-semibold text-content-primary`}>Type</th>}
                <th className={`${cellPad} text-left ${textSize} font-semibold text-content-primary`}>Statut</th>
                <th className={`${cellPad} text-left ${textSize} font-semibold text-content-primary`}>Actions</th>
              </tr>
            </thead>
            <tbody>{combinedRows}</tbody>
          </table>
        </div>
      </div>
    );
  };

  // Show folders when they exist
  if (
    resourceGroups &&
    onToggleFolder &&
    expandedFolders &&
    (!groupBy || groupBy === "none")
  ) {
    return renderTableWithFolders(trackers, resourceGroups);
  }

  if (!groupBy || !groupedTrackers || !onToggleGroup) {
    return renderTable(trackers);
  }

  // With grouping
  if (resourceGroups && onToggleFolder && expandedFolders) {
    return (
      <div className="space-y-4">
        {Object.entries(groupedTrackers).map(([groupKey, groupTrackers]) => {
          // Import getGroupKey from config
          const { getGroupKey } = trackerGalleryConfig;

          // Find folders that contain trackers from this group
          const foldersInGroup = resourceGroups.filter((folder) => {
            const trackersInFolder = trackers.filter((t) => {
              const groupId = getGroupRefId(t.group);
              return groupId === folder.id;
            });
            // Check if any tracker in this folder belongs to the current group
            return trackersInFolder.some((tracker) => {
              const trackerGroupKey = getGroupKey(tracker, groupBy);
              return trackerGroupKey === groupKey;
            });
          });

          // Get all trackers in this group (for rendering)
          const trackersInGroup = groupTrackers;

          // Total count includes folders and ungrouped trackers
          const ungroupedTrackersInGroup = groupTrackers.filter(
            (t) => !t.group,
          );
          const totalCount =
            foldersInGroup.length + ungroupedTrackersInGroup.length;

          if (totalCount === 0) return null;

          const isCollapsed = collapsedGroups.has(groupKey);

          return (
            <div key={groupKey} className="flex flex-col gap-3">
              <GroupHeader
                groupKey={groupKey}
                groupCount={totalCount}
                groupBy={groupBy}
                isCollapsed={isCollapsed}
                onToggle={() => onToggleGroup(groupKey)}
                resourceType="tracker"
                emptyGroupLabel="Tous les trackers"
              />
              <Collapse in={!isCollapsed}>
                {renderTableWithFolders(
                  trackersInGroup,
                  foldersInGroup,
                  (tracker) => {
                    const trackerGroupKey = getGroupKey(tracker, groupBy);
                    return trackerGroupKey === groupKey;
                  },
                )}
              </Collapse>
            </div>
          );
        })}
      </div>
    );
  }

  // Fallback when no folders
  if (!groupBy || !groupedTrackers || !onToggleGroup) {
    return renderTable(trackers);
  }

  return (
    <div className="flex flex-col gap-6">
      {Object.entries(groupedTrackers).map(([groupKey, groupTrackers]) => {
        const isCollapsed = collapsedGroups.has(groupKey);

        return (
          <div key={groupKey} className="flex flex-col gap-3">
            <GroupHeader
              groupKey={groupKey}
              groupCount={groupTrackers.length}
              groupBy={groupBy}
              isCollapsed={isCollapsed}
              onToggle={() => onToggleGroup(groupKey)}
              resourceType="tracker"
              emptyGroupLabel="Tous les trackers"
            />
            <Collapse in={!isCollapsed}>{renderTable(groupTrackers)}</Collapse>
          </div>
        );
      })}
    </div>
  );
};

export default TrackerTableView;
